import React from 'react';
import { useTranslate } from '../../mixins/translate/translate';
import { useOpenApiFetch } from '../../store/useOpenApiFetch';
import { Content, PanelBody, PanelWithoutForm, Topbar, TopbarCrumbActive, TopbarLeft, Tray } from '../elements';
import { Spinner } from '../spinners';

// Explicit local shapes for the /api/ai/usage/monthly response. TypeBox's inferred
// types from aiUsagePaths.ts don't always flow cleanly through our OpenApi
// machinery (IDE sometimes resolves to `any` / `never`), so we declare the exact
// shape here and cast once on receipt. Keep in sync with aiUsagePaths.ts.
interface IUsageRow {
    month: string;
    clientCompanyId: string;
    companyName: string | null;
    model: string;
    endpoint: string | null;
    tokensIn: number;
    tokensOut: number;
    estimatedCost: number;
    callCount: number;
}

interface IPricingRow {
    model: string;
    inputPerMillion: number;
    outputPerMillion: number;
}

interface IMonthlyUsageResult {
    rows: IUsageRow[];
    pricing: IPricingRow[];
}

function MonthlyUsagePanel() {
    const { data, isLoading } = useOpenApiFetch('/api/ai/usage/monthly', 'get', {}, []);
    const usage = data as IMonthlyUsageResult | undefined;

    if (isLoading) {
        return <Spinner />;
    }
    if (!usage || usage.rows.length === 0) {
        return <div style={{ padding: 15, color: '#666' }}>Nog geen AI-verbruik geregistreerd.</div>;
    }

    const priceByModel = new Map<string, IPricingRow>(usage.pricing.map((p): [string, IPricingRow] => [p.model, p]));
    const totalCost = usage.rows.reduce((sum, r) => sum + r.estimatedCost, 0);
    const totalTokensIn = usage.rows.reduce((sum, r) => sum + r.tokensIn, 0);
    const totalTokensOut = usage.rows.reduce((sum, r) => sum + r.tokensOut, 0);

    return (
        <div style={{ padding: 15 }}>
            <table className="table table-striped table-condensed">
                <thead>
                    <tr>
                        <th>Maand</th>
                        <th>Tenant</th>
                        <th>Model</th>
                        <th>Type</th>
                        <th style={{ textAlign: 'right' }}>Input tokens</th>
                        <th style={{ textAlign: 'right' }}>Output tokens</th>
                        <th style={{ textAlign: 'right' }}>Calls</th>
                        <th style={{ textAlign: 'right' }}>Kosten (USD)</th>
                    </tr>
                </thead>
                <tbody>
                    {usage.rows.map((row, i) => (
                        <tr key={`${row.month}-${row.clientCompanyId}-${row.model}-${row.endpoint ?? ''}-${i}`}>
                            <td>{row.month}</td>
                            <td>{row.companyName ?? <span style={{ color: '#999' }}>(onbekend)</span>}</td>
                            <td>
                                <code style={{ fontSize: 11 }}>{row.model}</code>
                            </td>
                            <td>{row.endpoint ?? '-'}</td>
                            <td style={{ textAlign: 'right' }}>{row.tokensIn.toLocaleString('nl-NL')}</td>
                            <td style={{ textAlign: 'right' }}>{row.tokensOut.toLocaleString('nl-NL')}</td>
                            <td style={{ textAlign: 'right' }}>{row.callCount.toLocaleString('nl-NL')}</td>
                            <td style={{ textAlign: 'right' }}>${row.estimatedCost.toFixed(4)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <th colSpan={4} style={{ textAlign: 'right' }}>
                            Totaal:
                        </th>
                        <th style={{ textAlign: 'right' }}>{totalTokensIn.toLocaleString('nl-NL')}</th>
                        <th style={{ textAlign: 'right' }}>{totalTokensOut.toLocaleString('nl-NL')}</th>
                        <th />
                        <th style={{ textAlign: 'right' }}>${totalCost.toFixed(4)}</th>
                    </tr>
                </tfoot>
            </table>
            {usage.pricing.length > 0 && (
                <div style={{ marginTop: 20 }}>
                    <h5>Tarieven (USD per 1M tokens)</h5>
                    <table className="table table-condensed" style={{ maxWidth: 600 }}>
                        <thead>
                            <tr>
                                <th>Model</th>
                                <th style={{ textAlign: 'right' }}>Input</th>
                                <th style={{ textAlign: 'right' }}>Output</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from(priceByModel.values()).map((p) => (
                                <tr key={p.model}>
                                    <td>
                                        <code style={{ fontSize: 11 }}>{p.model}</code>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>${p.inputPerMillion.toFixed(4)}</td>
                                    <td style={{ textAlign: 'right' }}>${p.outputPerMillion.toFixed(4)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 6 }}>
                        Tarieven instelbaar via <code>AI_PRICE_&lt;MODEL&gt;_INPUT_PER_M</code> / <code>_OUTPUT_PER_M</code> in de .env.
                    </div>
                </div>
            )}
        </div>
    );
}

export function AiSettings() {
    const translate = useTranslate();
    const { data: config, isLoading } = useOpenApiFetch('/api/ai/config', 'get', {}, []);

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title="AI" icon="fa-robot" />
                </TopbarLeft>
            </Topbar>
            <Content>
                <Tray>
                    {/* Configuration panel */}
                    <PanelWithoutForm>
                        <div className="panel-heading">
                            <h4>{translate('ai.configuration')}</h4>
                        </div>
                        <PanelBody>
                            <div className="admin-form">
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <strong>{translate('ai.chatModel')}</strong>
                                    </div>
                                    <div className="col-md-8">
                                        <code>{config?.chatModel ?? '-'}</code>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <strong>{translate('ai.embeddingModel')}</strong>
                                    </div>
                                    <div className="col-md-8">
                                        <code>{config?.embeddingModel ?? '-'}</code>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <strong>{translate('ai.region')}</strong>
                                    </div>
                                    <div className="col-md-8">
                                        <code>{config?.bedrockRegion ?? '-'}</code>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <strong>{translate('ai.status')}</strong>
                                    </div>
                                    <div className="col-md-8">
                                        {config?.isConfigured ? (
                                            <span className="label label-success">{translate('ai.connected')}</span>
                                        ) : (
                                            <span className="label label-warning">{translate('ai.notConfigured')}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </PanelBody>
                    </PanelWithoutForm>

                    {/* Monthly usage + pricing panel */}
                    <PanelWithoutForm>
                        <div className="panel-heading">
                            <h4>AI-verbruik per maand</h4>
                        </div>
                        <PanelBody>
                            <MonthlyUsagePanel />
                        </PanelBody>
                    </PanelWithoutForm>
                </Tray>
            </Content>
        </>
    );
}
